import { apiClient } from '../api-client'
import { User, PaginatedResponse } from '@/types'

// Users API (for role-based access)
export const usersApi = {
  getUsers: async (filters?: any, page = 1, limit = 20): Promise<PaginatedResponse<User>> => {
    const safeLimit = Math.max(1, Number(limit) || 20)
    const computedSkip = Number.isFinite(Number(filters?.skip))
      ? Number(filters.skip)
      : Math.max(0, (Math.max(1, Number(page) || 1) - 1) * safeLimit)
    const params = {
      ...filters,
      skip: computedSkip,
      limit: safeLimit,
      sort_by: filters?.sort_by,
      sort_order: filters?.sort_order,
    }
    delete (params as any).page

    const response = await apiClient.get<any>('/users', { params })

    if (Array.isArray(response)) {
      let total = response.length
      try {
        const countResponse = await apiClient.get<{ count?: number }>('/users', {
          params: {
            ...filters,
            count: true,
          },
        })
        total = Number(countResponse?.count ?? total)
      } catch {
        // Fallback to paged response size when count endpoint is unavailable.
      }

      const currentPage = Math.floor(computedSkip / safeLimit) + 1
      return {
        data: response,
        total,
        page: currentPage,
        limit: safeLimit,
        totalPages: Math.max(1, Math.ceil(total / safeLimit)),
      }
    }

    if (Array.isArray(response?.data)) {
      return {
        data: response.data,
        total: Number(response.total ?? response.data.length ?? 0),
        page: Number(response.page ?? Math.floor(computedSkip / safeLimit) + 1),
        limit: Number(response.limit ?? safeLimit),
        totalPages: Number(
          response.totalPages ??
          Math.max(1, Math.ceil((Number(response.total ?? response.data.length ?? 0)) / (Number(response.limit ?? safeLimit) || 1)))
        ),
      }
    }

    return {
      data: [],
      total: Number(response?.count ?? 0),
      page: Math.floor(computedSkip / safeLimit) + 1,
      limit: safeLimit,
      totalPages: 1,
    }
  },

  getAssignableUsers: (options?: { projectId?: string; taskId?: string }): Promise<User[]> =>
    apiClient.get('/users/assignable', {
      params: {
        project_id: options?.projectId,
        task_id: options?.taskId,
      },
    }),

  getDirectoryUsers: async (options?: { limit?: number; includeClients?: boolean }): Promise<User[]> => {
    const totalLimit = Math.max(1, Number(options?.limit) || 5000)
    const pageSize = Math.min(200, totalLimit)
    const includeClients = options?.includeClients === true

    const normalize = (rows: User[]) =>
      rows
        .filter((u: any) => u?.is_active !== false)
        .filter((u: any) => includeClients || String(u?.role || '').toLowerCase() !== 'client')

    try {
      let page = 1
      let totalPages = 1
      const allRows: User[] = []

      do {
        const response = await usersApi.getUsers({}, page, pageSize)
        const rows = response?.data || []
        allRows.push(...rows)
        totalPages = Math.max(1, Number(response?.totalPages || 1))
        page += 1
      } while (page <= totalPages && allRows.length < totalLimit)

      return normalize(allRows.slice(0, totalLimit))
    } catch {
      const fallback = await usersApi.getAssignableUsers()
      return normalize(Array.isArray(fallback) ? fallback : [])
    }
  },

  getUsersGroupedByRoles: (): Promise<Record<string, User[]>> =>
    apiClient.get('/users/grouped-by-roles'),

  createUser: (data: any): Promise<User> =>
    apiClient.post('/users', data),

  getUser: (id: string): Promise<User> =>
    apiClient.get(`/users/${id}`),

  updateUser: (id: string, data: Partial<User>): Promise<User> =>
    apiClient.put(`/users/${id}`, data),

  resetUserPassword: (id: string, newPassword: string): Promise<{ message: string }> =>
    apiClient.post(`/users/${id}/reset-password`, { new_password: newPassword }),

  activateUser: (id: string): Promise<User> =>
    apiClient.post(`/users/${id}/activate`, {}),

  deactivateUser: (id: string): Promise<User> =>
    apiClient.post(`/users/${id}/deactivate`, {}),

  blockUser: (id: string): Promise<User> =>
    apiClient.post(`/users/${id}/block`, {}),

  impersonateUser: (id: string): Promise<{
    access_token: string
    token_type: string
    expires_in: number
    impersonated_user: {
      id: string
      name: string
      email: string
      role: string
    }
  }> =>
    apiClient.post('/auth/impersonate', { userId: id }),

  deleteUser: (id: string): Promise<void> =>
    apiClient.delete(`/users/${id}`),
}
